import { trapFocus } from "../helpers/trapFocus"

const accessibleMenu = function (domNode) {
    this.rootNode = domNode
    this.triggerNodes = []
    this.controlledNodes = []
    this.openIndex = null
    this.useArrowKeys = true
}

accessibleMenu.prototype.init = function () {
    const buttons = this.rootNode.querySelectorAll("button[aria-expanded][aria-controls]")
    for (let i = 0; i < buttons.length; i++) {
        const button = buttons[i]
        const menu = button.parentNode.querySelector(".sub-menu")
        if (menu) {
            // Save ref to button and controlled menu.
            this.triggerNodes.push(button)
            this.controlledNodes.push(menu)

            // Collapse menus.
            button.setAttribute("aria-expanded", "false")
            this.toggleMenu(menu, false)

            // Attach event listeners.
            menu.addEventListener("keydown", this.handleMenuKeyDown.bind(this))
            button.addEventListener("click", this.handleButtonClick.bind(this))
            button.addEventListener("keydown", this.handleButtonKeyDown.bind(this))
        }
    }

    // Close open menu when clicking outside — handles non-focusable targets that never trigger focusout.
    document.addEventListener(
        "mousedown",
        (e) => {
            if (this.openIndex !== null && !this.rootNode.contains(e.target)) {
                this.toggleExpand(this.openIndex, false)
            }
        },
        true
    )

    this.rootNode.addEventListener("focusout", this.handleBlur.bind(this))

    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && this.openIndex !== null) {
            const trigger = this.triggerNodes[this.openIndex]
            event.preventDefault()
            this.toggleExpand(this.openIndex, false)
            trigger.focus()
        }
    })
}

accessibleMenu.prototype.toggleMenu = function (domNode, show) {
    if (domNode) {
        domNode.style.display = show ? "block" : "none"
        domNode.style.visibility = show ? "visible" : "hidden"
        domNode.classList.toggle("menu-is-open", show)
    }
}

accessibleMenu.prototype.toggleExpand = function (index, expanded) {
    // Close open menu, if applicable.
    if (this.openIndex !== index) {
        this.toggleExpand(this.openIndex, false)
    }

    // Handle menu at called index.
    if (this.triggerNodes[index]) {
        this.openIndex = expanded ? index : null
        this.triggerNodes[index].setAttribute("aria-expanded", expanded)
        this.toggleMenu(this.controlledNodes[index], expanded)
    }
}

accessibleMenu.prototype.controlFocusByKey = function (keyboardEvent, nodeList, currentIndex) {
    switch (keyboardEvent.key) {
        case "ArrowUp":
        case "ArrowLeft":
            keyboardEvent.preventDefault()
            if (currentIndex > -1) {
                const prevIndex = Math.max(0, currentIndex - 1)
                nodeList[prevIndex].focus()
            }
            break
        case "ArrowDown":
        case "ArrowRight":
            keyboardEvent.preventDefault()
            if (currentIndex > -1) {
                const nextIndex = Math.min(nodeList.length - 1, currentIndex + 1)
                nodeList[nextIndex].focus()
            }
            break
        case "Home":
            keyboardEvent.preventDefault()
            nodeList[0].focus()
            break
        case "End":
            keyboardEvent.preventDefault()
            nodeList[nodeList.length - 1].focus()
            break
    }
}

/* Event Handlers */
accessibleMenu.prototype.handleBlur = function (event) {
    // Null relatedTarget = click on non-focusable element; mousedown capture already handles that.
    // Only close when focus genuinely moves outside this menu (e.g. Tab away).
    if (
        event.relatedTarget &&
        !this.rootNode.contains(event.relatedTarget) &&
        this.openIndex !== null
    ) {
        this.toggleExpand(this.openIndex, false)
    }
}

accessibleMenu.prototype.handleButtonKeyDown = function (event) {
    const targetButtonIndex = this.triggerNodes.indexOf(document.activeElement)

    // Close the menu on the escape key.
    if (event.key === "Escape") {
        this.toggleExpand(this.openIndex, false)
    }

    // Move focus into the open menu if the current menu is open.
    else if (
        this.useArrowKeys &&
        this.openIndex === targetButtonIndex &&
        event.key === "ArrowDown"
    ) {
        event.preventDefault()
        this.controlledNodes[this.openIndex].querySelector("a").focus()
    }

    // Handle arrow key navigation between top-level buttons, if set.
    else if (this.useArrowKeys) {
        this.controlFocusByKey(event, this.triggerNodes, targetButtonIndex)
    }
}

accessibleMenu.prototype.handleButtonClick = function (event) {
    const button = event.target
    const buttonIndex = this.triggerNodes.indexOf(button)
    const buttonExpanded = button.getAttribute("aria-expanded") === "true"
    this.toggleExpand(buttonIndex, !buttonExpanded)
    // Note: trapFocus is intentionally omitted — disclosure pattern does not require Tab-trapping;
    // focus leaving the panel via Tab is handled by handleBlur closing the menu.
}

accessibleMenu.prototype.handleMenuKeyDown = function (event) {
    if (this.openIndex === null) {
        return
    }

    const menuLinks = Array.from(this.controlledNodes[this.openIndex].querySelectorAll("a"))
    const currentIndex = menuLinks.indexOf(document.activeElement)

    // Close the menu on the escape key.
    if (event.key === "Escape") {
        this.triggerNodes[this.openIndex].focus()
        this.toggleExpand(this.openIndex, false)
    }

    // Handle arrow key navigation within menu links, if set.
    else if (this.useArrowKeys) {
        this.controlFocusByKey(event, menuLinks, currentIndex)
    }
}

// Position the mega nav panel below its trigger button.
// Width is proportional to column count; position is centered below the button,
// clamped so the panel never overflows the container margins.
let _containerEl = null

function debounce(callback, delay) {
    let timeoutId

    return (...args) => {
        window.clearTimeout(timeoutId)
        timeoutId = window.setTimeout(() => callback(...args), delay)
    }
}

function positionMegaPanel(button, panel) {
    if (!_containerEl) {
        _containerEl =
            document.querySelector(".container--xl") || document.querySelector(".container")
    }

    const viewportWidth = document.documentElement.clientWidth
    const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
    const containerLeft = _containerEl ? _containerEl.getBoundingClientRect().left : 40

    const colCount = Math.max(1, panel.querySelectorAll(".mega-nav__col").length)
    // 17rem per column + gap-6 between + p-7 padding (3.5rem) + border-4 each side (0.5rem) + buffer (0.5rem)
    const naturalWidth = colCount * 17 * rem + (colCount - 1) * 1.5 * rem + 4.5 * rem
    const width = Math.min(naturalWidth, viewportWidth - 2 * containerLeft)
    panel.style.width = width + "px"
    panel.style.maxWidth = ""

    const btnRect = button.getBoundingClientRect()
    let left = btnRect.left + btnRect.width / 2 - width / 2
    left = Math.max(containerLeft, Math.min(left, viewportWidth - width - containerLeft))
    panel.style.left = left + "px"
    panel.style.transform = "none"
}

// Initialize Menus.
window.addEventListener(
    "load",
    function () {
        document.querySelectorAll(".menu").forEach((menu) => new accessibleMenu(menu).init())

        // Mega nav: only tracking needed is the mobile→desktop reset on resize.
        const megaNavPairs = Array.from(document.querySelectorAll("[aria-controls^='id_mega_']"))
            .map((btn) => ({
                button: btn,
                panel: document.getElementById(btn.getAttribute("aria-controls")),
            }))
            .filter(({ panel }) => panel !== null)

        const resetMegaPanels = () => {
            megaNavPairs.forEach(({ button, panel }) => {
                button.setAttribute("aria-expanded", "false")
                panel.style.display = "none"
                panel.style.visibility = "hidden"
                panel.style.width = ""
                panel.style.maxWidth = ""
                panel.style.left = ""
                panel.style.transform = ""
                panel.classList.remove("menu-is-open")
            })
        }

        megaNavPairs.forEach(({ button, panel }) => {
            button.addEventListener("click", () => {
                if (document.documentElement.clientWidth < 768) return
                if (button.getAttribute("aria-expanded") === "true")
                    positionMegaPanel(button, panel)
            })
        })

        // Track the previous breakpoint so we can detect a mobile→desktop crossing.
        let prevIsDesktop = window.innerWidth >= 768

        window.addEventListener(
            "resize",
            debounce(() => {
                const isDesktop = window.innerWidth >= 768

                if (isDesktop && !prevIsDesktop) {
                    // Crossed mobile-to-desktop: reset any panels that were open in the mobile menu
                    // so they start from a clean state and are positioned correctly when next opened.
                    resetMegaPanels()
                    // Also reset the mobile menu overlay
                    html.classList.remove("menu-is-open")
                    hamburgerButton.setAttribute("aria-expanded", "false")
                    hamburgerButton.classList.remove("active")
                    const mainEl = document.querySelector("main")
                    const footerEl = document.querySelector("footer")
                    if (mainEl) mainEl.removeAttribute("aria-hidden")
                    if (footerEl) footerEl.removeAttribute("aria-hidden")
                } else if (!isDesktop && prevIsDesktop) {
                    // Remove desktop panel geometry before the mobile menu can reopen it.
                    resetMegaPanels()
                } else if (isDesktop) {
                    megaNavPairs.forEach(({ button, panel }) => {
                        if (button.getAttribute("aria-expanded") === "true")
                            positionMegaPanel(button, panel)
                    })
                }

                prevIsDesktop = isDesktop
            }, 150)
        )

        // Toggle Hamburger Button & Dropdown.
        const hamburgerButton = document.querySelector(".hamburger-button")
        const logo = document.querySelector(".logo")
        const html = document.documentElement
        const screenreaderLabel = hamburgerButton.querySelector("#mobile-menu-toggle")

        hamburgerButton.addEventListener("click", function () {
            const nav = document.getElementsByClassName("nav")
            hamburgerButton.classList.toggle("active")

            const mainElement = document.querySelector("main")
            const footerElement = document.querySelector("footer")

            if (hamburgerButton.getAttribute("aria-expanded") === "false") {
                this.setAttribute("aria-expanded", "true")
                html.classList.add("menu-is-open")
                screenreaderLabel.textContent = "Close menu"
                trapFocus(nav[0], logo)
                mainElement.setAttribute("aria-hidden", "true")
                footerElement.setAttribute("aria-hidden", "true")
            } else {
                this.setAttribute("aria-expanded", "false")
                html.classList.remove("menu-is-open")
                screenreaderLabel.textContent = "Open menu"
                mainElement.setAttribute("aria-hidden", "false")
                footerElement.setAttribute("aria-hidden", "false")
            }
        })
    },
    false
)

window.addEventListener("scroll", () => {
    document.documentElement.classList.toggle("scrolled", scrollY > 0)
})